import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import BedIcon from '@mui/icons-material/Bed';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { getHotelOfferDetails, getHotelProductInfo } from '../../../api/endpoints/hotel';
import type { HotelOfferDetailResponse } from '../../../api/types/hotel';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import { buildHotelGuestLabels, buildHotelGuestSlots, type HotelFlowState } from '../flowConfig';
import { AboutTab, offerBoardName, offerRoomName } from './ResultsStep';

function formatDate(iso?: string) {
  return iso ? iso.slice(0, 10).split('-').reverse().join('.') : '—';
}

/** `Hotel/GetOfferDetails` price vs the search-result price — a rounded compare so a
 *  sub-lira rounding drift doesn't trip the "Fiyat güncellendi" warning. */
function priceDiffers(a?: number, b?: number): boolean {
  return a !== undefined && b !== undefined && Math.round(a) !== Math.round(b);
}

export function ReservationStep() {
  const flow = useBookingFlowContext<HotelFlowState>();
  const { selectedOffer, searchRequest, contact, guests } = flow.state;

  const productInfoQuery = useQuery({
    queryKey: ['hotelProductInfo', selectedOffer?.hotelId, selectedOffer?.arkmanHotel],
    queryFn: () => getHotelProductInfo(selectedOffer?.hotelId as string, selectedOffer?.arkmanHotel ?? false),
    enabled: !!selectedOffer?.hotelId,
    staleTime: 5 * 60 * 1000,
  });

  // `Hotel/GetOfferDetails` — güncel fiyat + iptal koşulu + ödeme vade tarihi. Allocate
  // ZATEN çalıştı (bu adım MakeReservation), ama teklif fiyatı sağlayıcıda kısa ömürlü
  // olduğundan burada tekrar sorgulanıp kullanıcıya doğrulatılır (bloke etmez).
  const offerDetailsQuery = useQuery({
    queryKey: ['hotelOfferDetails', selectedOffer?.offerId, selectedOffer?.currencyType],
    queryFn: () =>
      getHotelOfferDetails({
        offerId: selectedOffer?.offerId as string,
        currency: selectedOffer?.currencyType,
      }),
    enabled: !!selectedOffer?.offerId,
    staleTime: 60 * 1000,
  });
  const offerDetails: HotelOfferDetailResponse | undefined = offerDetailsQuery.data;

  const guestSlots = useMemo(() => buildHotelGuestSlots(searchRequest), [searchRequest]);
  const guestLabels = useMemo(() => buildHotelGuestLabels(guestSlots), [guestSlots]);

  const policyLines = useMemo(
    () => selectedOffer?.cancellationPolicy?.split(/\r?\n/).map((l) => l.trim()).filter(Boolean) ?? [],
    [selectedOffer?.cancellationPolicy]
  );

  const price = selectedOffer?.servicePrice ?? selectedOffer?.arkmanPrice;

  const currentPrice = offerDetails?.servicePrice;
  const priceUpdated = priceDiffers(currentPrice, price);
  const offerPolicyLines = useMemo(
    () =>
      (offerDetails?.cancellationPolicy ?? '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean),
    [offerDetails?.cancellationPolicy]
  );

  return (
    <Stack spacing={2}>
      {flow.error && <Alert severity="error">{flow.error}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Otel Bilgileri
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            {selectedOffer?.hotelImageUrl && (
              <Box
                component="img"
                src={selectedOffer.hotelImageUrl}
                alt={selectedOffer.hotelName}
                sx={{ width: { xs: '100%', sm: 220 }, height: { xs: 180, sm: 160 }, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                {selectedOffer?.hotelName ?? 'Otel'}
              </Typography>
              {selectedOffer?.star !== undefined && (
                <Rating value={selectedOffer.star} precision={0.5} size="small" readOnly sx={{ mt: 0.5 }} />
              )}
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 1 }}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {[selectedOffer?.cityName, selectedOffer?.countryName].filter(Boolean).join(', ') ||
                    selectedOffer?.address ||
                    '—'}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {selectedOffer ? (
            <AboutTab
              info={selectedOffer}
              productInfo={productInfoQuery.data}
              productInfoLoading={productInfoQuery.isFetching}
            />
          ) : (
            <Alert severity="warning">Otel bilgisi bulunamadı.</Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Seçilen Oda
          </Typography>
          <Stack spacing={1}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <BedIcon fontSize="small" color="primary" />
              <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                {(selectedOffer && offerRoomName(selectedOffer)) ?? '—'}
              </Typography>
            </Stack>
            {selectedOffer && offerBoardName(selectedOffer) && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <RestaurantIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {offerBoardName(selectedOffer)}
                </Typography>
              </Stack>
            )}
            <Typography variant="body2">
              Giriş Tarihi: {formatDate(searchRequest?.startDate)} · Çıkış Tarihi:{' '}
              {formatDate(searchRequest?.endDate)}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {guestSlots.length} Misafir ({(searchRequest?.passengersInfo ?? []).length || 1} Oda)
              </Typography>
            </Stack>

            {policyLines.length > 0 && (
              <Stack spacing={0.5}>
                {policyLines.map((line, index) => (
                  <Stack key={index} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <InfoIcon fontSize="small" color="error" />
                    <Typography variant="caption" color="error.main">
                      {line}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 1 }} />
            <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
              {price !== undefined ? `${price} ${selectedOffer?.currencyType ?? ''}` : '—'}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Güncel Teklif Bilgisi
          </Typography>
          {offerDetailsQuery.isFetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {offerDetailsQuery.isError && (
            <Alert severity="info">
              Güncel teklif bilgisi alınamadı. Arama sonucundaki fiyat geçerlidir.
            </Alert>
          )}
          {offerDetails && (
            <Stack spacing={1}>
              {priceUpdated && (
                <Alert severity="warning">
                  Fiyat güncellendi: eski ₺{price} → yeni ₺{currentPrice}
                </Alert>
              )}
              <Typography variant="body2">
                Güncel Fiyat:{' '}
                <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                  {currentPrice !== undefined
                    ? `${currentPrice} ${selectedOffer?.currencyType ?? ''}`
                    : '—'}
                </Typography>
              </Typography>
              {offerDetails.dueDate && (
                <Typography variant="body2">
                  Ödeme/İptal Son Tarihi: {formatDate(offerDetails.dueDate)}
                </Typography>
              )}
              {offerPolicyLines.length > 0 && (
                <Stack spacing={0.5}>
                  {offerPolicyLines.map((line, index) => (
                    <Stack key={index} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      <InfoIcon fontSize="small" color="error" />
                      <Typography variant="caption" color="error.main">
                        {line}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Misafirler
          </Typography>
          <Grid container spacing={1}>
            {(guests ?? []).map((guest, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2">
                  <Typography component="span" variant="body2" color="text.secondary">
                    {guestLabels[index] ?? `${index + 1}. Misafir`}:
                  </Typography>{' '}
                  {guest.firstName} {guest.lastName}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            İletişim Bilgileri
          </Typography>
          <Typography variant="body2">
            {contact?.name} {contact?.lastName} — {contact?.phone} — {contact?.email}
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" disabled={flow.status === 'running'} onClick={() => flow.runStep()}>
          {flow.status === 'running' ? 'Rezervasyon oluşturuluyor…' : 'Rezervasyonu Kesinleştir'}
        </Button>
      </Box>
    </Stack>
  );
}
